/**
 * The far end of an invitation link (D126) — `/i/<token>`, drawn by `views/Invitation.vue`.
 *
 * Three calls. Two are unauthenticated, because the person at this page is by construction somebody
 * who cannot sign in yet; the third is behind the session, for somebody who already could.
 */
import { API_BASE_URL } from "@/api/base";
import { authFetch, type FetchLike } from "./http";

export const INVITATION_PATH = "/invitations";
export const CLAIM_PATH = "/invitations/claim";
export const ACCEPT_PATH = "/memberships/accept";

export const INVITATION_TIMEOUT_MS = 20_000;

export interface OpenInvitation {
  organisationName: string;
  name: string;
  email: string;
  position: string | null;
  /** True: sign in and accept. False: choose a password here. */
  hasCredential: boolean;
  /** `membership` — a company's offer (D126) · `onboarding` — a production asking for the person's details (D161). */
  kind: "membership" | "onboarding";
}

export type ReadOutcome = { ok: true; invitation: OpenInvitation } | { ok: false; message: string };
export type ClaimOutcome = { ok: true; email: string; organisationName: string } | { ok: false; message: string };
export type AcceptOutcome = { ok: true } | { ok: false; message: string };

interface ErrorBody {
  message?: unknown;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

async function call(
  path: string,
  init: RequestInit,
  base: string,
  fetchImpl: FetchLike | undefined,
  authenticated: boolean,
): Promise<{ response: Response } | { failure: string }> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), INVITATION_TIMEOUT_MS);
  const send = fetchImpl ?? fetch;
  const request = { ...init, signal: abort.signal };
  try {
    const response = authenticated ? await authFetch(`${base}${path}`, request, send) : await send(`${base}${path}`, request);
    return { response };
  } catch (error) {
    return { failure: (error as Error)?.name === "AbortError" ? "the server did not answer in time" : "could not reach the server" };
  } finally {
    clearTimeout(timer);
  }
}

/** The server's own sentence when it has one; it is written for the person reading it. */
async function messageOf(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as ErrorBody;
    return text(body.message) ?? fallback;
  } catch {
    return fallback;
  }
}

export async function readInvitation(token: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<ReadOutcome> {
  const sent = await call(`${INVITATION_PATH}/${encodeURIComponent(token)}`, { method: "GET" }, base, fetchImpl, false);
  if ("failure" in sent) return { ok: false, message: sent.failure };
  if (!sent.response.ok) return { ok: false, message: await messageOf(sent.response, "this invitation is not valid any more") };
  const body = (await sent.response.json()) as Partial<OpenInvitation>;
  return {
    ok: true,
    invitation: {
      organisationName: text(body.organisationName) ?? "",
      name: text(body.name) ?? "",
      email: text(body.email) ?? "",
      position: text(body.position),
      hasCredential: body.hasCredential === true,
      kind: body.kind === "onboarding" ? "onboarding" : "membership",
    },
  };
}

export async function claimInvitation(token: string, newPassword: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<ClaimOutcome> {
  const sent = await call(
    CLAIM_PATH,
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, newPassword }) },
    base,
    fetchImpl,
    false,
  );
  if ("failure" in sent) return { ok: false, message: sent.failure };
  if (!sent.response.ok) return { ok: false, message: await messageOf(sent.response, "this invitation is not valid any more") };
  const body = (await sent.response.json()) as { email?: unknown; organisationName?: unknown };
  return { ok: true, email: text(body.email) ?? "", organisationName: text(body.organisationName) ?? "" };
}

/** Behind the session: the person is signed in as themselves, and this is their consent (D22). */
export async function acceptInvitation(token: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<AcceptOutcome> {
  const sent = await call(
    ACCEPT_PATH,
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) },
    base,
    fetchImpl,
    true,
  );
  if ("failure" in sent) return { ok: false, message: sent.failure };
  if (!sent.response.ok) return { ok: false, message: await messageOf(sent.response, "this invitation could not be accepted") };
  return { ok: true };
}
